import { api as commonApi } from '..';
import { ofType } from 'redux-observable';
import { EMPTY, from, iif, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { notify } from 'common/hooks/useNotification';
import svgIcons from 'common/svgIcons';
import { CommonEpic } from 'common/types';

import { actionLoadingGetBalance } from 'views/wallet-new/redux/actions';

import { routes } from 'routes/routes.constants';

import userActionTypes from './actionTypes';
import {
  actionErrorGetCurrentUser,
  actionErrorGetCustomerSettings,
  actionErrorGetCustomerTier,
  actionErrorGetSettings,
  actionErrorGetTiers,
  actionErrorGetVerificationLevel,
  actionErrorGetVerificationLimits,
  actionErrorSignIn,
  actionGetCurrentUser,
  actionGetCustomerSettings,
  actionGetCustomerTier,
  actionGetSettings,
  actionGetTiers,
  actionLoadingGetVerificationLevel,
  actionLoadingGetVerificationLimits,
  actionSuccessGetCurrentUser,
  actionSuccessGetCustomerSettings,
  actionSuccessGetCustomerTier,
  actionSuccessGetSettings,
  actionSuccessGetTiers,
  actionSuccessGetVerificationLevel,
  actionSuccessGetVerificationLimits,
  actionSuccessSignIn,
} from './actions';

export const epicGetProfile: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_CURRENT_USER),
    mergeMap(() =>
      from(api.getCurrentUser()).pipe(
        mergeMap((response) =>
          iif(
            () => Boolean(response.isAuthenticated),
            of(
              actionSuccessGetCurrentUser(response),
              actionLoadingGetBalance(),
              actionGetCustomerSettings(),
              actionGetSettings(),
              actionGetCustomerTier(),
              actionGetTiers(),
              actionLoadingGetVerificationLimits(),
              actionLoadingGetVerificationLevel(),
            ),
            of(actionSuccessGetCurrentUser(response), actionGetSettings(), actionGetCustomerSettings()),
          ),
        ),
        catchError((error) => of(actionErrorGetCurrentUser(error))),
      ),
    ),
  );

export const epicGetDocumentStatusChanged: CommonEpic<typeof commonApi> = (action$, _state$) =>
  action$.pipe(
    ofType(userActionTypes.DOCUMENTS_STATUS_CHANGED),
    mergeMap((action) => {
      if (window._disableVerificationChangedNotifications) return EMPTY;
      notify({
        titleFn: (translate) =>
          translate(`mobile:notifications:document:${action.payload.name}:declined`, true) ||
          translate(`mobile:notifications:document:${action.payload.type}:declined`),
        icon: svgIcons.Reject(),
        href: routes.getVerificationLevelPage(),
        autoClose: false,
      });
      return EMPTY;
    }),
  );

export const epicGetVerificationLevelChanged: CommonEpic<typeof commonApi> = (action$, _state$) =>
  action$.pipe(
    ofType(userActionTypes.VERIFICATION_LEVEL_CHANGED),
    mergeMap((action) => {
      if (window._disableVerificationChangedNotifications) return EMPTY;
      notify({
        titleFn: (translate) =>
          translate('mobile:notifications:level:changed').replace('{level}', action.payload.currentLevel),
        href: routes.getVerificationLevelPage(),
        autoClose: false,
      });
      return of(actionGetCurrentUser());
    }),
  );

export const epicGetSettings: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_SETTINGS),
    mergeMap(() =>
      from(api.getSettings()).pipe(
        mergeMap((response) => of(actionSuccessGetSettings(response))),
        catchError((error) => of(actionErrorGetSettings(error))),
      ),
    ),
  );

export const epicGetCustomerSettings: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_CUSTOMER_SETTINGS),
    mergeMap(() =>
      from(api.getCustomerSettings()).pipe(
        mergeMap((response) => of(actionSuccessGetCustomerSettings(response))),
        catchError((error) => of(actionErrorGetCustomerSettings(error))),
      ),
    ),
  );

export const epicSetCustomerSettings: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.SET_CUSTOMER_SETTINGS),
    mergeMap((action) =>
      from(api.setCustomerSettings(action.payload)).pipe(mergeMap(() => of(actionGetCustomerSettings()))),
    ),
  );

export const epicGetCustomerTier: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_CUSTOMER_TIER),
    mergeMap(() =>
      from(api.getTier()).pipe(
        mergeMap((response) => of(actionSuccessGetCustomerTier(response))),
        catchError((error) => of(actionErrorGetCustomerTier(error))),
      ),
    ),
  );

export const epicGetTiers: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_TIERS),
    mergeMap(() =>
      from(api.getTiers()).pipe(
        mergeMap((response) => of(actionSuccessGetTiers(response))),
        catchError((error) => of(actionErrorGetTiers(error))),
      ),
    ),
  );

export const epicLogin: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_SIGN_IN),
    mergeMap((action) =>
      from(api.postLogin(action.payload)).pipe(
        mergeMap(() => of(actionSuccessSignIn(), actionGetCurrentUser())),
        catchError((error) => of(actionErrorSignIn(error))),
      ),
    ),
  );

export const epicVerificationLimits: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_VERIFICATION_LIMITS),
    mergeMap(() =>
      from(api.getVerificationLimits()).pipe(
        mergeMap((response) => of(actionSuccessGetVerificationLimits(response))),
        catchError((error) => of(actionErrorGetVerificationLimits(error))),
      ),
    ),
  );

export const epicLoadingGetVerificationLevel: CommonEpic<typeof commonApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(userActionTypes.LOADING_GET_VERIFICATION_LEVEL),
    mergeMap(() =>
      from(api.getVerificationLevelStates()).pipe(
        mergeMap((response) => of(actionSuccessGetVerificationLevel(response))),
        catchError((error) => of(actionErrorGetVerificationLevel(error))),
      ),
    ),
  );
